import {
	SQ_AMBER_PAIR,
	SQ_GREY_STATIC_FROM,
} from "../../lib/sq-color-runtime-exports";
import { resolveHexagonInnerScale } from "../hexagon-map/lib/hexagon-layout-metrics";
import { resolveHexagonPathFaceScale } from "../hexagon-map/lib/hexagon-path-tile-appearance";
import { buildRoundedPolygonPath } from "../hexagon-map/lib/hexagon-rounded-clip";

/** Default interactive octagon control size (battle satellites, learn map corners). */
export const OCTAGON_BUTTON_DEFAULT_SIZE_PX = 60;

/** Default icon size inside {@link OCTAGON_BUTTON_DEFAULT_SIZE_PX}. */
export const OCTAGON_BUTTON_DEFAULT_ICON_PX = 34;

/** Glass band width between outer shell and stroke layer. */
export const OCTAGON_BUTTON_GLASS_BORDER_PX = 4;

/** Extra frosted glass halo outside skill badge chrome (additive; buttons unchanged). */
export const SKILL_BADGE_OUTER_GLASS_BORDER_PX = 2;

/** Rounded corners on stroke / face (visual, after layer scale). */
export const OCTAGON_BUTTON_INNER_CORNER_RADIUS_PX = 3;

/** Outer glass corners = inner + glass so the rim stays even at vertices. */
export const OCTAGON_BUTTON_OUTER_CORNER_RADIUS_PX =
	OCTAGON_BUTTON_INNER_CORNER_RADIUS_PX + OCTAGON_BUTTON_GLASS_BORDER_PX;

export const OCTAGON_BUTTON_INNER_STROKE_PX = 2;

export const OCTAGON_BUTTON_INNER_FILL = SQ_GREY_STATIC_FROM;
export const OCTAGON_BUTTON_INNER_STROKE = "#111111";

/** Selected face fill — matches header EXP `isLevelingUp` bar. */
export const OCTAGON_BUTTON_ACTIVE_GRADIENT_FROM = SQ_AMBER_PAIR.from;
export const OCTAGON_BUTTON_ACTIVE_GRADIENT_TO = SQ_AMBER_PAIR.to;

/** Learn sidebar highlighted stop wash (30% amber over card base). */
export const OCTAGON_BUTTON_SIDEBAR_HOVER_GRADIENT_FROM = SQ_AMBER_PAIR.from;
export const OCTAGON_BUTTON_SIDEBAR_HOVER_GRADIENT_TO = SQ_AMBER_PAIR.to;
export const OCTAGON_BUTTON_SIDEBAR_HOVER_GRADIENT_OPACITY = 0.3;

/** Shared dark rim on boost hubs, meter rings, pin, and +% badges. */
export const OCTAGON_BUTTON_CHROME_BORDER_COLOR = "#111111";

export const OCTAGON_BUTTON_CHROME_BORDER_CLASSNAME =
	"border-2 border-[#111111]";

export const OCTAGON_BUTTON_CHROME_BORDER_STROKE_PX = 2;

const OCTAGON_VIEWBOX = 100;

function octagonFlatTopVertices100(): Array<[number, number]> {
	return [
		[29.289322, 0],
		[70.710678, 0],
		[100, 29.289322],
		[100, 70.710678],
		[70.710678, 100],
		[29.289322, 100],
		[0, 70.710678],
		[0, 29.289322],
	];
}

function rotateAroundDeg(
	x: number,
	y: number,
	cx: number,
	cy: number,
	deg: number,
): [number, number] {
	const rad = (deg * Math.PI) / 180;
	const dx = x - cx;
	const dy = y - cy;
	const c = Math.cos(rad);
	const s = Math.sin(rad);
	return [c * dx - s * dy + cx, s * dx + c * dy + cy];
}

const OCTAGON_POINT_UP_ROT_DEG = 360 / 16;

function buildPointUpOctagonVerticesNormalized(): Array<[number, number]> {
	const rotated = octagonFlatTopVertices100().map(([x, y]) =>
		rotateAroundDeg(x, y, 50, 50, OCTAGON_POINT_UP_ROT_DEG),
	);
	let minX = Number.POSITIVE_INFINITY;
	let maxX = Number.NEGATIVE_INFINITY;
	let minY = Number.POSITIVE_INFINITY;
	let maxY = Number.NEGATIVE_INFINITY;
	for (const [x, y] of rotated) {
		minX = Math.min(minX, x);
		maxX = Math.max(maxX, x);
		minY = Math.min(minY, y);
		maxY = Math.max(maxY, y);
	}
	const bw = maxX - minX;
	const bh = maxY - minY;
	const scale = Math.min(100 / bw, 100 / bh) * 0.993;
	const wScaled = bw * scale;
	const hScaled = bh * scale;
	const ox = (100 - wScaled) / 2;
	const oy = (100 - hScaled) / 2;
	return rotated.map(([x, y]): [number, number] => [
		(x - minX) * scale + ox,
		(y - minY) * scale + oy,
	]);
}

/** Point-up octagon in 0–100 space. */
export const OCTAGON_POINT_UP_VERTICES_100 =
	buildPointUpOctagonVerticesNormalized();

function octagonVerticesPx(sizePx: number): Array<[number, number]> {
	const scale = sizePx / OCTAGON_VIEWBOX;
	return OCTAGON_POINT_UP_VERTICES_100.map(([x, y]): [number, number] => [
		x * scale,
		y * scale,
	]);
}

function toCssPathClip(pathData: string): string {
	const escaped = pathData.replace(/"/g, '\\"');
	return `path("${escaped}")`;
}

export interface OctagonButtonClipPaths {
	outerClipPath: string;
	innerClipPath: string;
	strokeClipPath: string;
	faceClipPath: string;
	strokeScale: number;
	faceScale: number;
}

export interface OctagonClipGeometryOptions {
	glassBorderPx: number;
	innerStrokePx: number;
	innerCornerRadiusPx: number;
	/** Floor for the inner face corner after stroke inset (keeps the dark fill soft). */
	minFaceCornerRadiusPx?: number;
}

export const OCTAGON_BUTTON_CLIP_GEOMETRY: OctagonClipGeometryOptions = {
	glassBorderPx: OCTAGON_BUTTON_GLASS_BORDER_PX,
	innerStrokePx: OCTAGON_BUTTON_INNER_STROKE_PX,
	innerCornerRadiusPx: OCTAGON_BUTTON_INNER_CORNER_RADIUS_PX,
};

/** Skill badges — thicker gold rim and dark outer stroke for reward-tier chrome. */
export const SKILL_BADGE_CLIP_GEOMETRY: OctagonClipGeometryOptions = {
	glassBorderPx: 3,
	innerStrokePx: 3,
	innerCornerRadiusPx: 5,
	minFaceCornerRadiusPx: 3.5,
};

/** Inner corner of the outer glass band — aligns with skill-badge outer shell radius. */
export const SKILL_BADGE_OUTER_GLASS_INNER_CORNER_RADIUS_PX =
	SKILL_BADGE_CLIP_GEOMETRY.innerCornerRadiusPx +
	SKILL_BADGE_CLIP_GEOMETRY.glassBorderPx;

/** Outer glass outer corner = inner + band width (same rule as octagon buttons). */
export const SKILL_BADGE_OUTER_GLASS_OUTER_CORNER_RADIUS_PX =
	SKILL_BADGE_OUTER_GLASS_INNER_CORNER_RADIUS_PX +
	SKILL_BADGE_OUTER_GLASS_BORDER_PX;

/** Outer glass halo geometry — frosted band outside reward chrome. */
export const SKILL_BADGE_OUTER_GLASS_CLIP_GEOMETRY: OctagonClipGeometryOptions =
	{
		glassBorderPx: SKILL_BADGE_OUTER_GLASS_BORDER_PX,
		innerStrokePx: OCTAGON_BUTTON_INNER_STROKE_PX,
		innerCornerRadiusPx: SKILL_BADGE_OUTER_GLASS_INNER_CORNER_RADIUS_PX,
	};

export function resolveSkillBadgeOuterGlassSizePx(sizePx: number): number {
	return sizePx + SKILL_BADGE_OUTER_GLASS_BORDER_PX * 2;
}

/** Layout bleed so the outer glass halo is not clipped by `size × size` hosts. */
export function resolveSkillBadgeVisualFootprintPx(sizePx: number): number {
	return resolveSkillBadgeOuterGlassSizePx(sizePx);
}

/**
 * Layered clip paths + scales — mirrors hex path tiles for point-up octagons.
 */
export function buildOctagonClipPaths(
	sizePx = OCTAGON_BUTTON_DEFAULT_SIZE_PX,
	geometry: OctagonClipGeometryOptions = OCTAGON_BUTTON_CLIP_GEOMETRY,
): OctagonButtonClipPaths {
	const thumbPx = Math.max(1, sizePx);
	const glassBorderPx = geometry.glassBorderPx;
	const innerStrokePx = geometry.innerStrokePx;
	const innerVisualCornerPx = geometry.innerCornerRadiusPx;
	const outerPx = innerVisualCornerPx + glassBorderPx;
	const shellInnerCornerPx = Math.max(0, outerPx - glassBorderPx);
	const vertices = octagonVerticesPx(thumbPx);
	const outerPath = buildRoundedPolygonPath(vertices, outerPx);
	const shellInnerPath = buildRoundedPolygonPath(
		vertices,
		shellInnerCornerPx,
	);
	const strokeScale = resolveHexagonInnerScale(thumbPx, glassBorderPx);
	const faceScale = resolveHexagonPathFaceScale({
		innerScale: strokeScale,
		hexagonWidth: thumbPx,
		hexagonHeight: thumbPx,
		strokeWidthPx: innerStrokePx,
	});
	const strokeClipCornerPx =
		strokeScale > 0
			? innerVisualCornerPx / strokeScale
			: innerVisualCornerPx;
	const strokePath = buildRoundedPolygonPath(vertices, strokeClipCornerPx);
	const faceVisualCornerPx = Math.max(
		geometry.minFaceCornerRadiusPx ?? 0,
		innerVisualCornerPx - innerStrokePx,
	);
	const faceClipCornerPx =
		faceScale > 0 ? faceVisualCornerPx / faceScale : faceVisualCornerPx;
	const facePath = buildRoundedPolygonPath(vertices, faceClipCornerPx);

	return {
		outerClipPath: toCssPathClip(outerPath),
		innerClipPath: toCssPathClip(shellInnerPath),
		strokeClipPath: toCssPathClip(strokePath),
		faceClipPath: toCssPathClip(facePath),
		strokeScale,
		faceScale,
	};
}

function insetVerticesTowardCenter(
	vertices: ReadonlyArray<readonly [number, number]>,
	insetVb: number,
): Array<[number, number]> {
	const cx = OCTAGON_VIEWBOX / 2;
	const cy = OCTAGON_VIEWBOX / 2;
	return vertices.map(([x, y]): [number, number] => {
		const dx = x - cx;
		const dy = y - cy;
		const len = Math.hypot(dx, dy);
		if (len < 0.001) {
			return [x, y];
		}
		const t = Math.max(0, len - insetVb) / len;
		return [cx + dx * t, cy + dy * t];
	});
}

/** Ripple / hit target clip — outer rounded octagon (`path()`). */
export function buildOctagonRippleClipStyle(
	sizePx = OCTAGON_BUTTON_DEFAULT_SIZE_PX,
): { clipPath: string; WebkitClipPath: string } {
	const { outerClipPath } = buildOctagonClipPaths(sizePx);
	return {
		clipPath: outerClipPath,
		WebkitClipPath: outerClipPath,
	};
}

/** Inner face bounding span in px. */
export function resolveOctagonInnerSpanPx(
	sizePx = OCTAGON_BUTTON_DEFAULT_SIZE_PX,
): number {
	const insetVb = (OCTAGON_BUTTON_GLASS_BORDER_PX / sizePx) * OCTAGON_VIEWBOX;
	const innerVertices = insetVerticesTowardCenter(
		OCTAGON_POINT_UP_VERTICES_100,
		insetVb,
	);
	let minX = Number.POSITIVE_INFINITY;
	let maxX = Number.NEGATIVE_INFINITY;
	let minY = Number.POSITIVE_INFINITY;
	let maxY = Number.NEGATIVE_INFINITY;
	for (const [x, y] of innerVertices) {
		minX = Math.min(minX, x);
		maxX = Math.max(maxX, x);
		minY = Math.min(minY, y);
		maxY = Math.max(maxY, y);
	}
	const scale = sizePx / OCTAGON_VIEWBOX;
	return Math.max(maxX - minX, maxY - minY) * scale;
}

/** Pill bottom aligns with hub bottom: px offset upward from outer ring bottom. */
export function resolveOctagonBadgeBottomOffsetPx(ringSizePx: number): number {
	const hubDiameterFrac = 0.84;
	const hubPx = Math.round(ringSizePx * hubDiameterFrac);
	return Math.round(ringSizePx / 2 - hubPx / 2);
}
